import ClientService from "@/services/ClientService";
import SiteService from "@/services/SiteService";
import BuildingService from "@/services/BuildingService";
import LevelService from "@/services/LevelService";
import ContentService from "@/services/ContentService";
import PublishService from "@/services/PublishService";
import PersonaService from "@/services/PersonaService";

import Helpers from "@/helpers/Helpers";
import store from "@/store/store.js";
import MapHelpers from "@/helpers/MapHelpers";
import axios from "axios";

const contentNameTypeCodeDict = {
  pois: "poiTypeCodesObj",
  mapObjects: "mapObjectsTypeCodesObj",
  obstacles: "obstaclesTypeCodesObj"
};

const filteredContentTypeCodeDict = {
  pois: "filteredPoiTypeCodeObjs",
  mapObjects: "filteredMapObjectsTypeCodeObjs",
  obstacles: "filteredObstaclesTypeCodeObjs"
};

export default {
  namespaced: true,
  state: () => ({
    clients: [],
    publishedClient: undefined,
    site: undefined,
    sites: [],
    currentSiteData: { clientId: undefined, siteId: undefined, buildings: [], levels: [] },
    sitePublishStatuses: [],
    buildings: [],
    levels: [],
    pois: [],
    graphs: [],
    beacons: [],
    mapObjects: [],
    originalMapObjects: [],
    obstacles: [],
    beaconGeofences: [],
    gpsGeofences: [],
    obstaclesTypeCodesObj: {},
    filteredObstaclesTypeCodeObjs: [],
    filteredGraphs: [],
    filterText: "",
    poiTypeCodesObj: {},
    mapObjectsTypeCodesObj: {},
    filteredMapObjectsTypeCodeObjs: [],
    filteredPoiTypeCodeObjs: [],
    filteredBeacons: [],
    filteredBeaconGeofences: [],
    originalMapObjectsBuildingInternalIdentifierLevelIndex: {},
    defaultContentVisibility: {
      pois: true,
      beacons: true,
      mapObjects: true,
      obstacles: true,
      beaconGeofences: false,
      graphs: false
    },
    contentVisibility: {
      pois: true,
      beacons: true,
      mapObjects: true,
      obstacles: true,
      beaconGeofences: false,
      graphs: false
    },
    selectedContent: undefined,
    isFormDirty: false,
    // Used for updating component on route changes by breadcrumb
    refreshKey: 0,
    isDragging: false,
    shownPathLevel: undefined,
    isShownPathLevelSelectorExpanded: false,
    dashboardMode: "normal",
    metadataPublishStatus: undefined,
    personasPublishStatus: undefined,
    configurationClientPublishStatus: undefined,
    configurationSitePublishStatus: undefined,
    initializedContentParameters: { clientId: undefined, siteId: undefined, buildingId: undefined, levelId: undefined },
    mapPersonas: [],
    selectedPersona: undefined, // Public
    cachedSelectedPersona: undefined,
    shouldRemoveSelectedItems: false,
    personaSelectionTooltipText: undefined,
    shouldHidePersonaSelectionTooltip: false,
    isAnyLevelUpdated: false,
    isAddedOnMap: {
      mapObjects: false,
      gpsGeofences: false,
      beaconGeofences: false
    },
    copiedMapContentFeatures: [],
    selectedFeatures: []
  }),
  mutations: {
    CLIENTS(state, clients = []) {
      state.clients = [...clients];
    },
    PUBLISHED_CLIENT(state, publishedClient) {
      state.publishedClient = publishedClient;
    },
    SITE(state, site = {}) {
      state.site = site;
    },
    SITES(state, sites = []) {
      state.sites = [...sites];
    },
    CURRENT_SITE_DATA(state, payload) {
      const { clientId, siteId, site } = payload;
      state.currentSiteData.clientId = clientId;
      state.currentSiteData.siteId = siteId;

      state.currentSiteData.buildings = [];
      state.currentSiteData.levels = [];

      site?.features?.forEach((feature) => {
        if (feature.properties.typeCode === "building-outline") {
          state.currentSiteData.buildings.push(feature);
        } else if (feature.properties.typeCode === "level-outline") {
          state.currentSiteData.levels.push(feature);
        }
      });
      state.currentSiteData.buildings.sort((a, b) => a.properties.name.localeCompare(b.properties.name));
      state.currentSiteData.levels.sort((a, b) => a.properties.lvl - b.properties.lvl);
      state.isAnyLevelUpdated = false;
    },
    RESET_CURRENT_SITE_DATA(state) {
      state.currentSiteData = { clientId: undefined, siteId: undefined, buildings: [], levels: [], features: [] };
    },
    SITE_PUBLISH_STATUSES(state, sitePublishStatuses = []) {
      if (sitePublishStatuses.length) {
        state.sitePublishStatuses = [...sitePublishStatuses];
      } else {
        state.sitePublishStatuses = [];
      }
    },
    BUILDINGS(state, buildings = []) {
      state.buildings = [...buildings];
    },
    LEVELS(state, levels = []) {
      state.levels = [...levels];
    },
    POIS(state, pois = []) {
      // Freeze POI objects to prevent Vue reactivity overhead and improve performance
      const frozenPois = pois.map((poi) => {
        // Freeze nested objects but keep properties mutable for Vue
        if (poi.geometry && typeof poi.geometry === "object") {
          Object.freeze(poi.geometry);
        }
        return poi;
      });

      state.pois = [...frozenPois];
    },
    LOCAL_POIS(state, payload) {
      if (payload.isEdit) {
        state.pois.every((p, index) => {
          if (p?.properties?.fid === payload.feature?.properties?.fid) {
            state.pois[index] = payload.feature;
            return false;
          }
          return true;
        });
      } else if (payload.isDelete) {
        const indexToDelete = state.pois.findIndex((poi) => poi.properties.fid === payload?.feature?.properties?.fid);
        if (indexToDelete !== -1) {
          state.pois.splice(indexToDelete, 1);
        }
      } else {
        state.pois.push(payload.feature);
      }
      state.pois = [...state.pois];
    },
    GRAPHS(state, graphs = []) {
      // Freeze graph objects to prevent Vue reactivity overhead and improve performance
      const frozenGraphs = graphs.map((graph) => {
        // Freeze nested objects but keep properties mutable for Vue
        if (graph.geometry && typeof graph.geometry === "object") {
          Object.freeze(graph.geometry);
        }
        return graph;
      });

      state.graphs = [...frozenGraphs];
    },
    LOCAL_GRAPHS(state, graphs = []) {
      state.graphs = [...graphs];
      const escapedFilterText = Helpers.escapeRegExp(state.filterText);
      const filter = RegExp(`(${escapedFilterText})`, "i");
      state.filteredGraphs = store.getters["CONTENT/graphsForSelectedPersona"](state.graphs).filter((node) => {
        return filter.test(node?.properties?.name) || filter.test(node?.properties?.fid);
      });
    },
    BEACONS(state, beacons = []) {
      // Freeze beacon objects to prevent Vue reactivity overhead and improve performance
      const frozenBeacons = beacons.map((beacon) => {
        // Freeze nested objects but keep properties mutable for Vue
        if (beacon.geometry && typeof beacon.geometry === "object") {
          Object.freeze(beacon.geometry);
        }
        return beacon;
      });

      state.beacons = [...frozenBeacons];
    },
    LOCAL_BEACONS(state, payload) {
      if (payload.isEdit) {
        state.beacons.every((b, index) => {
          if (b?.properties?.fid === payload.feature?.properties?.fid) {
            state.beacons[index] = payload.feature;
            return false;
          }
          return true;
        });
      } else if (payload.isDelete) {
        const indexToDelete = state.beacons.findIndex((b) => b.properties.fid === payload.feature?.properties?.fid);
        if (indexToDelete !== -1) {
          state.beacons.splice(indexToDelete, 1);
        }
      } else {
        state.beacons.push(payload.feature);
      }
      state.beacons = [...state.beacons];
    },
    MAP_OBJECTS(state, mapObjects = []) {
      // Freeze map object objects to prevent Vue reactivity overhead and improve performance
      const frozenMapObjects = mapObjects.map((mapObject) => {
        // Freeze nested objects but keep properties mutable for Vue
        if (mapObject.geometry && typeof mapObject.geometry === "object") {
          Object.freeze(mapObject.geometry);
        }
        return mapObject;
      });

      state.mapObjects = [...frozenMapObjects];
    },
    ORIGINAL_MAP_OBJECTS(state, payload) {
      const { mapObjects = [], buildingInternalIdentifier, levelIndex } = payload || {};
      if (
        state.originalMapObjectsBuildingInternalIdentifierLevelIndex?.[buildingInternalIdentifier] &&
        mapObjects?.length > 0
      ) {
        state.originalMapObjectsBuildingInternalIdentifierLevelIndex[buildingInternalIdentifier][levelIndex] = [
          ...mapObjects
        ];
      } else {
        state.originalMapObjectsBuildingInternalIdentifierLevelIndex[buildingInternalIdentifier] = {};
        if (mapObjects?.length > 0) {
          state.originalMapObjectsBuildingInternalIdentifierLevelIndex[buildingInternalIdentifier][levelIndex] = [
            ...mapObjects
          ];
        }
      }
      state.originalMapObjects = [...(mapObjects || [])];
    },
    LOCAL_MAP_OBJECTS(state, payload) {
      if (payload.isEdit) {
        state.mapObjects.every((p, index) => {
          if (p?.properties?.fid === payload.feature?.properties?.fid) {
            payload.feature.id = index;
            state.mapObjects[index] = payload.feature;
            return false;
          }
          return true;
        });
      } else if (payload.isDelete) {
        const indexToDelete = state.mapObjects.findIndex(
          (mapObject) => mapObject.properties.fid === payload?.feature?.properties?.fid
        );
        if (indexToDelete !== -1) {
          state.mapObjects.splice(indexToDelete, 1);
        }
      } else {
        state.mapObjects.push(payload.feature);
      }
      state.mapObjects = [...state.mapObjects];
    },
    OBSTACLES(state, obstacles = []) {
      // Freeze obstacle objects to prevent Vue reactivity overhead and improve performance
      const frozenObstacles = obstacles.map((obstacle) => {
        // Freeze nested objects but keep properties mutable for Vue
        if (obstacle.geometry && typeof obstacle.geometry === "object") {
          Object.freeze(obstacle.geometry);
        }
        return obstacle;
      });

      state.obstacles = [...frozenObstacles];
    },
    LOCAL_OBSTACLES(state, obstacle) {
      state.obstacles.every((o, index) => {
        if (o?.properties?.fid === obstacle?.properties?.fid) {
          state.obstacles[index] = obstacle;
          return false;
        }
        return true;
      });
    },
    BEACON_GEOFENCES(state, beaconGeofences = []) {
      // Freeze beacon geofence objects to prevent Vue reactivity overhead and improve performance
      const frozenBeaconGeofences = beaconGeofences.map((geofence) => {
        // Freeze nested objects but keep properties mutable for Vue
        if (geofence.geometry && typeof geofence.geometry === "object") {
          Object.freeze(geofence.geometry);
        }
        return geofence;
      });

      state.beaconGeofences = [...frozenBeaconGeofences];
    },
    LOCAL_BEACON_GEOFENCES(state, payload) {
      if (payload.isEdit) {
        state.beaconGeofences.every((lg, index) => {
          if (lg?.properties?.fid === payload.feature?.properties?.fid) {
            state.beaconGeofences[index] = payload.feature;
            return false;
          }
          return true;
        });
      } else if (payload.isDelete) {
        const indexToDelete = state.beaconGeofences.findIndex(
          (lg) => lg.properties.fid === payload.feature.properties?.fid
        );
        if (indexToDelete !== -1) {
          state.beaconGeofences.splice(indexToDelete, 1);
        }
      } else {
        state.beaconGeofences.push(payload.feature);
      }
      state.beaconGeofences = [...state.beaconGeofences];
    },
    GLOBAL_GEOFENCES(state, gpsGeofences = []) {
      // Freeze geofence objects to prevent Vue reactivity overhead and improve performance
      const frozenGeofences = gpsGeofences.map((geofence) => {
        // Freeze nested objects but keep properties mutable for Vue
        if (geofence.geometry && typeof geofence.geometry === "object") {
          Object.freeze(geofence.geometry);
        }
        return geofence;
      });

      state.gpsGeofences = [...frozenGeofences].sort((geofence1, geofence2) => {
        return geofence1?.properties?.name?.toLowerCase().localeCompare(geofence2?.properties?.name?.toLowerCase());
      });
    },
    // creates type code dicts for content types{meeting-space: [], chair: []}
    CREATE_TYPE_CODE_DICTS_FOR_TWO_LEVELS(state) {
      // Update features and type codes accordingly using the contentNameTypeCodeDict
      Object.entries(contentNameTypeCodeDict).forEach(([contentKey, contentName]) => {
        state[contentName] = state[contentKey].reduce((featuresObj, feature) => {
          if (!featuresObj[feature.properties.typeCode]) {
            featuresObj[feature.properties.typeCode] = {
              features: []
            };
            if (state?.[contentName]?.[feature.properties.typeCode]?.isVisible === undefined) {
              featuresObj[feature.properties.typeCode].isVisible = state.contentVisibility[contentKey];
            } else {
              featuresObj[feature.properties.typeCode].isVisible =
                state?.[contentName]?.[feature.properties.typeCode]?.isVisible;
            }
          }
          if (feature.geometry?.type === "Point" || feature.geometry?.type === "Polygon") {
            featuresObj[feature.properties.typeCode].features.push(feature);
          }
          return featuresObj;
        }, {});
        // sort according to key (typeCodes)
        state[contentName] = Object.keys(state[contentName])
          .sort((a, b) => a.localeCompare(b))
          .reduce((obj, key) => {
            obj[key] = state[contentName][key];
            return obj;
          }, {});
      });
    },
    FILTER_TEXT(state, value) {
      state.filterText = value.toLowerCase();
    },
    // two levels features are gruped by typeCode
    FILTER_TWO_LEVEL_FEATURES(state) {
      // there is no need to filter the features object if the filter text is empty
      if (!state.filterText) {
        Object.keys(filteredContentTypeCodeDict).forEach((content) => {
          const filteredContentType = filteredContentTypeCodeDict[content]; // e.g. filteredPoiTypeCodeObjs
          const typeCodesObjs = contentNameTypeCodeDict[content]; // e.g. poiTypeCodesObj
          state[filteredContentType] = Object.values(state[typeCodesObjs]); //assigned poiTypeCodesObj values to filteredPoiTypeCodeObjs
        });
        return;
      }
      Object.keys(filteredContentTypeCodeDict).forEach((content) => {
        const filteredContentType = filteredContentTypeCodeDict[content]; // e.g. filteredPoiTypeCodeObjs
        state[filteredContentType] = [];
      });

      Object.keys(filteredContentTypeCodeDict).forEach((content) => {
        const typeCodesObjs = contentNameTypeCodeDict[content];
        for (const typeCode in state[typeCodesObjs]) {
          const higherPriority = [];
          const lowerPriority = [];
          state[typeCodesObjs][typeCode].features.forEach((feature) => {
            const strArrToMatch = Helpers.generateStringArrayWithFilterRules(feature);
            if (strArrToMatch.some((str) => str?.toString().startsWith(state.filterText))) {
              higherPriority.push(feature);
            } else if (strArrToMatch.some((str) => str?.toString().includes(state.filterText))) {
              lowerPriority.push(feature);
            }
          });
          state[filteredContentTypeCodeDict[content]].push({
            isVisible: state[typeCodesObjs][typeCode].isVisible,
            features: [...higherPriority, ...lowerPriority]
          });
        }
      });
    },
    // This is for features without a type code group
    FILTER_ONE_LEVEL_FEATURES(state) {
      const keys = ["beacons", "beaconGeofences"];
      const dict = {
        beacons: "filteredBeacons",
        beaconGeofences: "filteredBeaconGeofences"
      };
      if (!state.filterText) {
        keys.forEach((key) => {
          if (key === "beaconGeofences") {
            state[dict[key]] = [...store.getters["CONTENT/beaconGeofencesForSelectedPersona"]()];
          } else {
            state[dict[key]] = [...state[key]];
          }
        });
        return;
      }
      keys.forEach((key) => {
        const filteredKey = dict[key];
        const higherPriority = [];
        const lowerPriority = [];
        const featuresToFilter =
          key === "beaconGeofences" ? store.getters["CONTENT/beaconGeofencesForSelectedPersona"]() : state[key];
        featuresToFilter.forEach((feature) => {
          const strArrToMatch = Helpers.generateStringArrayWithFilterRules(feature);
          if (strArrToMatch.some((str) => str?.toString().startsWith(state.filterText))) {
            higherPriority.push(feature);
          } else if (strArrToMatch.some((str) => str?.toString().includes(state.filterText))) {
            lowerPriority.push(feature);
          }
        });
        state[filteredKey] = [...higherPriority, ...lowerPriority];
      });
    },
    //graph is a special case. Because we only display one graph but it has many feature in it actually.
    FILTER_GRAPH(state, rootState) {
      const escapedFilterText = Helpers.escapeRegExp(state.filterText);
      const filter = RegExp(`(${escapedFilterText})`, "i");
      state.filteredGraphs = store.getters["CONTENT/graphsForSelectedPersona"]().filter((node) => {
        if (node?.properties?.typeCode === "path-node") {
          if (node?.properties?.isOutdoor) {
            return filter.test("Paths") || filter.test(node?.properties?.name) || filter.test("outdoor");
          } else {
            return filter.test("Paths") || filter.test(node?.properties?.name) || filter.test("indoor");
          }
        } else {
          return (
            filter.test(node?.properties?.name) ||
            filter.test(rootState.taxonomy[node?.properties?.typeCode]?.title) ||
            filter.test(node?.properties?.typeCode) ||
            filter.test(node?.properties?.fid)
          );
        }
      });
    },
    COPIED_MAP_CONTENT_FEATURES(state, features) {
      state.copiedMapContentFeatures = features;
    },
    TOGGLE_TYPE_CODE_VISIBILITY(state, payload) {
      const contentTypeObjName = contentNameTypeCodeDict[payload.contentName];
      state[contentTypeObjName][payload.typeCode].isVisible = payload.isVisible;
      if (payload.isVisible) {
        state.contentVisibility[payload.contentName] = true;
      } else {
        const isAllInvisible = Object.values(state[contentTypeObjName]).every((typeCodeObj) => !typeCodeObj.isVisible);
        state.contentVisibility[payload.contentName] = !isAllInvisible;
      }
    },
    TOGGLE_CONTENT_VISIBILITY(state, payload) {
      state.contentVisibility[payload.contentName] = payload.isVisible;
      const contentTypeObjName = contentNameTypeCodeDict[payload.contentName];
      if (contentTypeObjName) {
        for (const typeCode in state[contentTypeObjName]) {
          state[contentTypeObjName][typeCode].isVisible = payload.isVisible;
        }
      }
    },
    RESET_CONTENT_VISIBILITY(state) {
      state.contentVisibility = { ...state.defaultContentVisibility };
      Object.entries(contentNameTypeCodeDict).forEach(([contentKey, contentName]) => {
        state[contentName] = state[contentKey].reduce((featuresObj, feature) => {
          if (!featuresObj[feature.properties.typeCode]) {
            featuresObj[feature.properties.typeCode] = {
              isVisible: state.contentVisibility[contentKey],
              features: []
            };
          }
          featuresObj[feature.properties.typeCode].features.push(feature);
          return featuresObj;
        }, {});
        // sort according to key (typeCodes)
        state[contentName] = Object.keys(state[contentName])
          .sort((a, b) => a.localeCompare(b))
          .reduce((obj, key) => {
            obj[key] = state[contentName][key];
            return obj;
          }, {});
        state[filteredContentTypeCodeDict[contentKey]] = Object.values(state[contentName]);
        // sort according to names under typeCodes
        state[filteredContentTypeCodeDict[contentKey]].forEach((featuresGroupedByTypeCode) => {
          featuresGroupedByTypeCode?.features?.sort((a, b) =>
            String(a.properties?.name)?.localeCompare(b.properties?.name)
          );
        });
      });
      MapHelpers.toggleAllLayers(state.mapObjectsTypeCodesObj);
    },
    SELECTED_CONTENT(state, content) {
      state.selectedContent = content;
    },
    IS_FORM_DIRTY(state, isDirty) {
      state.isFormDirty = isDirty;
    },
    INCR_REFRESH_KEY(state) {
      state.refreshKey++;
    },
    IS_DRAGGING(state, value) {
      state.isDragging = value;
    },
    SHOWN_PATH_LEVEL(state, value) {
      state.shownPathLevel = value;
    },
    IS_SHOWN_PATH_LEVEL_SELECTOR_EXPANDED(state, isExpanded) {
      state.isShownPathLevelSelectorExpanded = isExpanded;
    },
    DASHBOARD_MODE(state, value) {
      state.dashboardMode = value;
    },
    METADATA_PUBLISH_STATUS(state, value) {
      if (value?.lastPublishedDate) {
        value.lastPublishedDate =
          new Date(value.lastPublishedDate).toLocaleTimeString(window.navigator.language) +
          " - " +
          new Date(value.lastPublishedDate).toLocaleDateString(window.navigator.language);
      }
      state.metadataPublishStatus = value;
    },
    PERSONAS_PUBLISH_STATUS(state, value) {
      if (value?.lastPublishedDate) {
        value.lastPublishedDate =
          new Date(value.lastPublishedDate).toLocaleTimeString(window.navigator.language) +
          " - " +
          new Date(value.lastPublishedDate).toLocaleDateString(window.navigator.language);
      }
      state.personasPublishStatus = value;
    },
    CONFIGURATION_PUBLISH_STATUS(state, value) {
      if (value?.lastPublishedDate) {
        value.lastPublishedDate =
          new Date(value.lastPublishedDate).toLocaleTimeString(window.navigator.language) +
          " - " +
          new Date(value.lastPublishedDate).toLocaleDateString(window.navigator.language);
      }
      state.configurationClientPublishStatus = value;
    },
    CONFIGURATION_SITE_PUBLISH_STATUS(state, value) {
      if (value?.lastPublishedDate) {
        value.lastPublishedDate =
          new Date(value.lastPublishedDate).toLocaleTimeString(window.navigator.language) +
          " - " +
          new Date(value.lastPublishedDate).toLocaleDateString(window.navigator.language);
      }
      state.configurationSitePublishStatus = value;
    },
    INITIALIZED_CONTENT_PARAMETERS(state, payload) {
      state.initializedContentParameters = payload;
    },
    MAP_PERSONAS(state, payload) {
      state.mapPersonas = payload;
    },
    SELECTED_PERSONA(state, payload) {
      state.selectedPersona = payload;
      localStorage.setItem("selectedPersona", payload);
    },
    CACHED_SELECTED_PERSONA(state, payload) {
      state.cachedSelectedPersona = payload;
    },
    SHOULD_REMOVE_SELECTED_ITEMS(state, payload) {
      state.shouldRemoveSelectedItems = payload;
    },
    PERSONA_SELECTION_TOOLTIP_TEXT(state, payload) {
      state.personaSelectionTooltipText = payload;
    },
    SHOULD_HIDE_PERSONA_SELECTION_TOOLTIP(state, payload) {
      state.shouldHidePersonaSelectionTooltip = payload;
    },
    IS_ANY_LEVEL_UPDATED(state, payload) {
      state.isAnyLevelUpdated = payload;
    },
    IS_ADDED_ON_MAP(state, payload) {
      const updates = Array.isArray(payload) ? payload : [payload];
      updates.forEach(({ contentName, isAddedOnMap }) => {
        state.isAddedOnMap[contentName] = isAddedOnMap;
      });
    },
    SELECTED_FEATURES(state, payload) {
      state.selectedFeatures = payload;
    }
  },
  actions: {
    UPDATE_FEATURE({ commit, dispatch }, payload) {
      commit("LOCAL_POIS", payload);
      commit("LOCAL_MAP_OBJECTS", payload);
      dispatch("GENERATE_TYPE_CODE_DICTS");
    },
    UPDATE_BEACON_GEOFENCES({ commit, dispatch }, payload) {
      commit("LOCAL_BEACON_GEOFENCES", payload);
      dispatch("GENERATE_TYPE_CODE_DICTS");
    },
    GENERATE_TYPE_CODE_DICTS({ commit }) {
      commit("CREATE_TYPE_CODE_DICTS_FOR_TWO_LEVELS");
    },
    FILTER_TEXT({ commit, rootState }, value) {
      commit("FILTER_TEXT", value);
      commit("FILTER_TWO_LEVEL_FEATURES");
      commit("FILTER_ONE_LEVEL_FEATURES");
      commit("FILTER_GRAPH", rootState);
    },
    async SET_CLIENTS({ commit, state }, payload) {
      if ((state.clients.length && payload?.forceUpdate) || !state.clients.length) {
        const response = await ClientService.getClients();
        commit("CLIENTS", response);
      }
      // We're not multiclient yet, so we only have one client
      commit("MAP/CURRENT_CLIENT", state?.clients[0]?.identifier, { root: true });
    },
    async SET_PUBLISHED_CLIENT({ commit, state }, payload) {
      if ((state?.publishedClient && payload?.forceUpdate) || !state.publishedClient) {
        const publishedClient = await ClientService.getPublishedClient(payload.clientInternalIdentifier);
        commit("PUBLISHED_CLIENT", publishedClient);
      }
    },
    async SET_SITE({ commit, state }, payload) {
      if (!payload.siteId) {
        console.debug("Could not set site - invalid params");
        return;
      }
      if (state.sites?.find((site) => site?.properties.fid === payload?.siteId) && !payload?.forceUpdate) {
        commit(
          "SITE",
          state.sites?.find((site) => site?.properties.fid === payload?.siteId)
        );
        return;
      }
      if ((state.site && payload?.forceUpdate) || !state.site) {
        const response = await SiteService.getSite(payload.clientId, payload.siteId);
        commit("SITE", response);
      }
    },
    async SET_CURRENT_SITE_DATA({ commit }, payload) {
      const { clientId, siteId, abortController } = payload;
      if (payload.forceUpdate) {
        const site = await SiteService.getDraftSite(clientId, siteId, abortController);
        commit("CURRENT_SITE_DATA", { clientId, siteId, site });
      }
    },
    async SET_SITES({ commit, state }, payload) {
      if (!payload.clientId) {
        console.debug("Could not set sites - invalid params");
        return;
      }

      if ((state.sites.length && payload?.forceUpdate) || !state.sites.length) {
        const response = await SiteService.getSites(payload.clientId);
        commit("SITES", response);
      }
    },

    async SET_SITE_PUBLISH_STATUSES({ commit }, payload) {
      if (!payload.clientId) {
        console.debug("Could not set site publish statuses - invalid params");
        return;
      }
      const response = await PublishService.getSitePublishStatuses(payload.clientId);
      commit("SITE_PUBLISH_STATUSES", response);
    },
    async SET_BUILDINGS({ commit, state }, payload) {
      if (!payload.siteId) {
        console.debug("Could not set buildings - invalid params");
        return;
      }
      if ((state.buildings.length && payload?.forceUpdate) || !state.buildings.length) {
        const response = await BuildingService.getBuildings(payload.clientId, payload.siteId);
        commit("BUILDINGS", response);
      }
    },
    async SET_LEVELS({ commit, state }, payload) {
      if (!payload.buildingId) {
        console.debug("Could not set levels - invalid params");
        return;
      }
      if ((state.levels.length && payload?.forceUpdate) || !state.levels.length) {
        const response = await LevelService.getLevels(payload.clientId, payload.siteId, payload.buildingId);
        commit("LEVELS", response);
      }
    },
    async SET_CONTENTS({ dispatch }, payload) {
      if (
        payload.siteInternalIdentifier === undefined ||
        payload.buildingInternalIdentifier === undefined ||
        payload.levelIndex === undefined
      ) {
        console.debug("Could not set contents - invalid params");
        return;
      }
      await Promise.all([
        dispatch("SET_POIS", {
          forceUpdate: payload.forceUpdate,
          clientInternalIdentifier: payload.clientInternalIdentifier,
          siteInternalIdentifier: payload.siteInternalIdentifier,
          buildingInternalIdentifier: payload.buildingInternalIdentifier,
          levelIndex: payload.levelIndex
        }),
        dispatch("SET_GRAPHS", {
          forceUpdate: payload.forceUpdate,
          clientInternalIdentifier: payload.clientInternalIdentifier,
          siteInternalIdentifier: payload.siteInternalIdentifier,
          buildingInternalIdentifier: payload.buildingInternalIdentifier
        }),
        dispatch("SET_BEACONS", {
          forceUpdate: payload.forceUpdate,
          clientInternalIdentifier: payload.clientInternalIdentifier,
          siteInternalIdentifier: payload.siteInternalIdentifier,
          buildingInternalIdentifier: payload.buildingInternalIdentifier,
          levelIndex: payload.levelIndex
        }),
        dispatch("SET_OBSTACLES", {
          forceUpdate: payload.forceUpdate,
          clientInternalIdentifier: payload.clientInternalIdentifier,
          siteInternalIdentifier: payload.siteInternalIdentifier,
          buildingInternalIdentifier: payload.buildingInternalIdentifier,
          levelIndex: payload.levelIndex
        }),
        dispatch("SET_BEACON_GEOFENCES", {
          forceUpdate: payload.forceUpdate,
          clientInternalIdentifier: payload.clientInternalIdentifier,
          siteInternalIdentifier: payload.siteInternalIdentifier,
          buildingInternalIdentifier: payload.buildingInternalIdentifier,
          levelIndex: payload.levelIndex
        })
      ]);
    },
    async SET_POIS({ commit, state }, payload) {
      if ((state.pois.length && payload?.forceUpdate) || !state.pois.length || payload.levelIndex) {
        // We don't need to remove it everytime. But it is better to remove old data in case of any error.
        const response = await ContentService.getFeatures(
          ContentService.CONTENT_TYPES.POI,
          payload.clientInternalIdentifier,
          payload.siteInternalIdentifier,
          payload.buildingInternalIdentifier,
          payload.levelIndex
        );
        commit("POIS", response?.features);
      } else {
        console.warn("Could not set pois - invalid params");
      }
    },
    async SET_GRAPHS({ commit, state }, payload) {
      if ((state.graphs?.length && payload?.forceUpdate) || !state.graphs?.length) {
        const response = await ContentService.getGraphs(
          payload.clientInternalIdentifier,
          payload.siteInternalIdentifier
        );
        const validFids = new Set(response?.features?.map((feature) => feature?.properties?.fid));

        // Group indoor path-nodes by their existing pathGroupId, preserving existing groups
        const indoorPathNodes = response?.features?.filter((feature) => {
          return (
            feature?.properties?.typeCode === "path-node" &&
            feature?.properties?.lvl !== undefined &&
            feature?.properties?.bid !== undefined &&
            !feature?.properties?.isOutdoor
          );
        });

        // First pass: collect paths without pathGroupId, grouped by building and level
        const pathsWithoutGroupByBuildingAndLevel = {};
        indoorPathNodes?.forEach((f) => {
          if (!f?.properties?.pathGroupId) {
            const building = f?.properties?.bid;
            const level = f?.properties?.lvl;
            const key = `${building}_${level}`;
            if (!pathsWithoutGroupByBuildingAndLevel[key]) {
              pathsWithoutGroupByBuildingAndLevel[key] = [];
            }
            pathsWithoutGroupByBuildingAndLevel[key].push(f);
          }
        });

        // Assign stable group IDs to paths without groupId, grouped by building and level
        Object.keys(pathsWithoutGroupByBuildingAndLevel).forEach((key) => {
          const pathsForBuildingAndLevel = pathsWithoutGroupByBuildingAndLevel[key];
          if (pathsForBuildingAndLevel.length > 0) {
            const stableGroupId = pathsForBuildingAndLevel[0]?.properties?.fid;
            pathsForBuildingAndLevel.forEach((feature) => {
              feature.properties.pathGroupId = stableGroupId;
            });
          }
        });

        // Second pass: group all paths by their pathGroupId
        const indoorPathNodesByGroup = {};
        indoorPathNodes?.forEach((feature) => {
          const groupId = feature?.properties?.pathGroupId;
          if (!indoorPathNodesByGroup[groupId]) {
            indoorPathNodesByGroup[groupId] = [];
          }
          indoorPathNodesByGroup[groupId].push(feature);
        });

        // Group outdoor path-nodes by pathGroupId and generate group-specific UUIDs
        const outdoorPathNodes = response?.features?.filter((feature) => {
          return (
            feature?.properties?.typeCode === "path-node" &&
            feature?.properties?.isOutdoor &&
            feature?.properties?.lvl === undefined &&
            feature?.properties?.bid === undefined
          );
        });

        // First pass: collect outdoor paths without pathGroupId to group them together
        const outdoorPathsWithoutGroup = outdoorPathNodes?.filter((f) => !f?.properties?.pathGroupId);
        let outdoorStableGroupId = null;
        if (outdoorPathsWithoutGroup.length > 0) {
          // Use the first path's fid as a stable identifier
          outdoorStableGroupId = outdoorPathsWithoutGroup[0]?.properties?.fid;
          // Assign the stable group ID to all paths without a group
          outdoorPathsWithoutGroup.forEach((feature) => {
            feature.properties.pathGroupId = outdoorStableGroupId;
          });
        }

        // Second pass: group all outdoor paths by their pathGroupId
        const outdoorPathNodesByGroup = {};
        outdoorPathNodes?.forEach((feature) => {
          const groupId = feature?.properties?.pathGroupId;
          if (!outdoorPathNodesByGroup[groupId]) {
            outdoorPathNodesByGroup[groupId] = [];
          }
          outdoorPathNodesByGroup[groupId].push(feature);
        });

        const filteredFeatures = response?.features?.filter((feature) => {
          if (feature?.properties?.typeCode === "path-node") {
            if (!feature?.properties?.name) {
              feature.properties.name = feature.properties.isOutdoor ? "Outdoor Path" : "Indoor Path";
            }
          }
          // change lift-nodes to elevator-node
          if (feature.properties.typeCode === "lift-node") {
            feature.properties.typeCode = "elevator-node";
          }
          // TODO: DELETE THE LINES BELOW AFTER BACKEND IS FIXED
          // FOR NOW, WHEN A LEVEL IS DELETED INDOOR BUILDING ENTRANCE EXIT NODES ARE DELETED BUT THEIR PORTAL NEIGHBORS ARE NOT
          if (feature?.properties?.typeCode === "building-entrance-exit") {
            return feature?.properties?.transitionNeighbors?.every((neighbor) => validFids.has(neighbor?.fid));
          }
          return true;
        });
        commit("GRAPHS", filteredFeatures);
      }
    },
    async SET_BEACONS({ commit, state }, payload) {
      if ((state.beacons.length && payload?.forceUpdate) || !state.beacons.length) {
        const response = await ContentService.getBeacons(
          payload.clientInternalIdentifier,
          payload.siteInternalIdentifier
        );
        response?.features?.sort((beacon1, beacon2) => {
          const major1 = beacon1.properties.major;
          const minor1 = beacon1.properties.minor;
          const major2 = beacon2.properties.major;
          const minor2 = beacon2.properties.minor;
          if (major1 > major2) return 1;
          else if (major1 === major2) return minor1 - minor2;
          else return -1;
        });
        commit("BEACONS", response?.features);
      }
    },
    async SET_SITE_MAP_OBJECTS({ commit, state, dispatch, getters }, payload) {
      if ((state?.mapObjects?.length && payload?.forceUpdate) || !state.mapObjects.length) {
        const { clientId, siteId, abortController } = payload;
        const response = await ContentService.getSiteMapObjects(clientId, siteId, abortController);
        let featuresToAdd;
        dispatch("MAP/REMOVE_MAP_OBJECTS_FROM_MAP", null, { root: true });
        response?.features?.forEach((feature, index) => {
          feature.id = index;
        });
        featuresToAdd = response?.features;
        commit("MAP_OBJECTS", featuresToAdd);
        dispatch("MAP/ADD_MAP_OBJECTS_TO_MAP", getters.mapObjectsForSelectedPersona(featuresToAdd), { root: true });
        commit("IS_ADDED_ON_MAP", { contentName: "mapObjects", isAddedOnMap: true });
      }
    },
    async UPDATE_MAP_OBJECTS({ commit, state, dispatch, getters }, payload) {
      const updatedFeature = payload.feature;

      const existingFeatureIndex = state.mapObjects.findIndex(
        (feature) => feature.properties.fid === updatedFeature.properties.fid
      );

      let updatedMapObjects;
      if (existingFeatureIndex !== -1) {
        updatedMapObjects = state.mapObjects.map((feature, index) => {
          if (feature.properties.fid === updatedFeature.properties.fid) {
            return { ...updatedFeature, id: index };
          }
          return feature;
        });
      } else {
        const newFeature = { ...updatedFeature, id: state.mapObjects.length };
        updatedMapObjects = [...state.mapObjects, newFeature];
      }

      commit("MAP_OBJECTS", updatedMapObjects);
      dispatch("MAP/ADD_MAP_OBJECTS_TO_MAP", getters.mapObjectsForSelectedPersona(updatedMapObjects), { root: true });
      commit("IS_ADDED_ON_MAP", { contentName: "mapObjects", isAddedOnMap: true });
      dispatch("GENERATE_TYPE_CODE_DICTS");
    },
    async SET_ORIGINAL_MAP_OBJECTS({ state, commit }, payload) {
      if (!payload.request) {
        console.debug("Could not set original map objects - invalid params");
        return;
      }
      // if it is already cached, return the cached data
      if (
        state.originalMapObjectsBuildingInternalIdentifierLevelIndex?.[payload.buildingInternalIdentifier]?.[
          payload.levelIndex
        ]
      ) {
        commit("ORIGINAL_MAP_OBJECTS", {
          mapObjects:
            state.originalMapObjectsBuildingInternalIdentifierLevelIndex[payload.buildingInternalIdentifier][
              payload.levelIndex
            ],
          buildingInternalIdentifier: payload.buildingInternalIdentifier,
          levelIndex: payload.levelIndex
        });
        return;
      }
      const response = await LevelService.cadToGeojson(payload.clientInternalIdentifier, payload.request);

      const responseUrl = response?.outputFileUrl;
      if (!responseUrl) {
        console.debug("Could not set original map objects - no output file URL");
        return;
      }

      const geojsonResponse = await axios.get(responseUrl);
      const geojsonData = geojsonResponse.data;

      const features = (geojsonData?.features || []).map((feature) => {
        if (!feature.properties.typeCode) {
          return { ...feature, properties: { ...feature.properties, typeCode: "blueprint" } };
        }
        if (!feature.properties.fid) {
          feature.properties.fid = Helpers.generateUuid();
        }
        return feature;
      });
      commit("ORIGINAL_MAP_OBJECTS", {
        mapObjects: features,
        buildingInternalIdentifier: payload.buildingInternalIdentifier,
        levelIndex: payload.levelIndex
      });
    },
    async SET_OBSTACLES({ commit, state }, payload) {
      if ((state.obstacles.length && payload?.forceUpdate) || !state.obstacles.length) {
        if (payload.obstacles) {
          commit("OBSTACLES", payload.obstacles.features);
        } else {
          const response = await ContentService.getFeatures(
            ContentService.CONTENT_TYPES.OBSTACLE,
            payload.clientInternalIdentifier,
            payload.siteInternalIdentifier,
            payload.buildingInternalIdentifier,
            payload.levelIndex
          );
          commit("OBSTACLES", response?.features);
        }
      }
    },
    async SET_BEACON_GEOFENCES({ commit, state }, payload) {
      if ((state.beaconGeofences.length && payload?.forceUpdate) || !state.beaconGeofences.length) {
        // TODO FIX THIS
        if (payload.beaconGeofences) {
          commit("BEACON_GEOFENCES", payload.beaconGeofences.features);
        } else {
          const response = await ContentService.getBeaconGeofences(
            payload.clientInternalIdentifier,
            payload.siteInternalIdentifier
          );
          commit("BEACON_GEOFENCES", response?.features);
        }
      }
    },
    async SET_GLOBAL_GEOFENCES({ commit, state }, payload) {
      if ((state.gpsGeofences.length && payload?.forceUpdate) || !state.gpsGeofences.length) {
        if (payload.gpsGeofences) {
          commit("GLOBAL_GEOFENCES", payload.gpsGeofences.features);
        } else {
          const response = await ContentService.getgpsGeofences(payload.clientId, payload.siteId);
          commit("GLOBAL_GEOFENCES", response?.features);
        }
      }
    },
    //TODO: Remove
    SET_SELECTED_CONTENT({ commit, getters }, payload) {
      if (payload.featureId) {
        const content = getters.getContentFromFid(payload.featureId);
        commit("SELECTED_CONTENT", content);
      } else {
        commit("SELECTED_CONTENT", undefined);
      }
    },
    SET_LOCAL_CHANGES({ commit, dispatch, getters }) {
      dispatch("MAP/ADD_MAP_OBJECTS_TO_MAP", getters.mapObjectsForSelectedPersona(), {
        root: true
      });
      commit("IS_ADDED_ON_MAP", { contentName: "mapObjects", isAddedOnMap: true });
    },
    SET_VISIBILITY_OF_TYPE_CODE({ state }, payload) {
      const contentTypeObjName = contentNameTypeCodeDict[payload.contentName];
      if (state[contentTypeObjName][payload.typeCode]) {
        state[contentTypeObjName][payload.typeCode].isVisible = payload.isVisible;
      }
    },
    SET_SHOWN_PATH_LEVEL({ state, commit }, levelIndex) {
      if (state.isShownPathLevelSelectorExpanded) {
        commit("SHOWN_PATH_LEVEL", levelIndex);
      }
      commit("IS_SHOWN_PATH_LEVEL_SELECTOR_EXPANDED", !state.isShownPathLevelSelectorExpanded);
    },
    async FETCH_METADATA_PUBLISH_STATUS({ commit }, payload) {
      const response = await PublishService.getMetadataPublishStatus(payload.clientId);
      commit("METADATA_PUBLISH_STATUS", response);
    },
    async FETCH_PERSONAS_PUBLISH_STATUS({ commit }, payload) {
      const response = await PublishService.getPersonasPublishStatus(payload.clientId);
      commit("PERSONAS_PUBLISH_STATUS", response);
    },
    async FETCH_CONFIGURATION_PUBLISH_STATUS({ commit }, payload) {
      // Refactor for new mapscale
      const response = await PublishService.getConfigurationClientPublishStatus(payload.clientId);
      commit("CONFIGURATION_PUBLISH_STATUS", response);
    },
    async FETCH_CONFIGURATION_SITE_PUBLISH_STATUS({ commit }, payload) {
      const response = await PublishService.getSiteConfigurationPublishStatus(payload.clientId, payload.siteId);
      commit("CONFIGURATION_SITE_PUBLISH_STATUS", response);
    },
    async SET_MAP_PERSONAS({ commit, dispatch }, payload) {
      const response = await PersonaService.getPersonas(payload.clientInternalIdentifier);
      if (!response || response.length === 0) {
        console.error("No personas found");
        return;
      }
      commit("MAP_PERSONAS", response);
      if (response?.length > 1) {
        const selectedPersona = response?.find(
          (persona) => persona?.personaIdentifier === localStorage.getItem("selectedPersona")
        );
        if (!selectedPersona) {
          localStorage.setItem("selectedPersona", "all-personas");
        }
        dispatch("SET_SELECTED_PERSONA", localStorage.getItem("selectedPersona"));
      } else {
        dispatch("SET_SELECTED_PERSONA", response[0].personaIdentifier);
      }
    },
    async UPDATE_MAP_PERSONA({ dispatch, rootState }, payload) {
      const persona = { ...payload.persona };
      const personaIdentifier = payload.persona.personaIdentifier;
      delete persona.personaIdentifier;
      const response = await PersonaService.updatePersona(persona, rootState.MAP.currentClient, personaIdentifier);
      if (response) {
        dispatch("SET_MAP_PERSONAS", { clientInternalIdentifier: rootState.MAP.currentClient });
      }
    },
    async ADD_MAP_PERSONA({ dispatch, rootState }, payload) {
      const persona = { ...payload.persona };
      delete persona.personaIdentifier;

      const response = await PersonaService.addPersona(persona, rootState.MAP.currentClient);

      if (response?.personaIdentifier !== undefined) {
        dispatch("SET_MAP_PERSONAS", { clientInternalIdentifier: rootState.MAP.currentClient });
      }
    },
    SET_SELECTED_PERSONA({ state, commit, getters, dispatch }, payload) {
      commit("SELECTED_PERSONA", payload);
      if (state.isAddedOnMap.mapObjects) {
        dispatch("MAP/REMOVE_MAP_OBJECTS_FROM_MAP", null, { root: true });
        dispatch("MAP/ADD_MAP_OBJECTS_TO_MAP", getters.mapObjectsForSelectedPersona(), { root: true });
        commit("IS_ADDED_ON_MAP", { contentName: "mapObjects", isAddedOnMap: true });
      }
      if (state.isAddedOnMap.beaconGeofences || state.isAddedOnMap.gpsGeofences) {
        dispatch("MAP/ADD_GEOFENCES_TO_MAP", null, { root: true });
      }
    },
    ADD_SELECTED_FEATURE({ state, commit }, payload) {
      const mapObject = state.mapObjects.find((mapObject) => mapObject.properties.fid === payload.properties.fid);
      commit("SELECTED_FEATURES", [...state.selectedFeatures, mapObject]);
    },
    REMOVE_SELECTED_FEATURE({ state, commit }, payload) {
      commit(
        "SELECTED_FEATURES",
        state.selectedFeatures.filter((feature) => feature.properties.fid !== payload.properties.fid)
      );
    },
    CLEAR_SELECTED_FEATURES({ commit }) {
      commit("SELECTED_FEATURES", []);
    }
  },
  getters: {
    getTypeCodeObjVisibility: (state) => (typeCode, contentName) => {
      const contentTypeObjName = contentNameTypeCodeDict[contentName];
      return state[contentTypeObjName][typeCode]?.isVisible;
    },
    getContentFromFid: (state) => (fid) => {
      const content =
        state.pois.find((feature) => fid === feature.properties.fid) ||
        state.beacons.find((feature) => fid === feature.properties.fid) ||
        state.mapObjects.find((feature) => fid === feature.properties.fid) ||
        state.beaconGeofences.find((feature) => fid === feature.properties.fid) ||
        state.obstacles.find((feature) => fid === feature.properties.fid);
      return content;
    },
    mapObjects: (state) => state.mapObjects,
    graphs: (state) => state.graphs,
    selectedPersonaObject: (state) => {
      return state.selectedPersona === "all-personas"
        ? { personaIdentifier: "all-personas", name: "All Personas", color: "#1051e8" }
        : state.mapPersonas.find((p) => p.personaIdentifier === state.selectedPersona);
    },
    defaultPersona: (state) => state.mapPersonas.find((p) => p.isDefault),
    filterFeaturesByPersona:
      (state) =>
      (features, selectedPersona = state.selectedPersona) => {
        if (selectedPersona === "all-personas" || !selectedPersona) {
          return features;
        }
        return features.filter((feature) => {
          const { epids, ipids } = feature.properties;
          if (ipids !== undefined && ipids.length > 0) {
            return ipids.includes(selectedPersona);
          }
          if (epids !== undefined && epids.length > 0) {
            return !epids.includes(selectedPersona);
          }
          return true;
        });
      },
    mapObjectsForSelectedPersona:
      (state, getters) =>
      (mapObjectsToFilter = state.mapObjects) => {
        return getters.filterFeaturesByPersona(mapObjectsToFilter);
      },
    beaconGeofencesForSelectedPersona:
      (state, getters) =>
      (beaconGeofencesToFilter = state.beaconGeofences) => {
        return getters.filterFeaturesByPersona(beaconGeofencesToFilter);
      },
    gpsGeofencesForSelectedPersona:
      (state, getters) =>
      (gpsGeofencesToFilter = state.gpsGeofences) => {
        return getters.filterFeaturesByPersona(gpsGeofencesToFilter);
      },
    graphsForSelectedPersona:
      (state, getters) =>
      (graphsToFilter = state.graphs) => {
        return getters.filterFeaturesByPersona(graphsToFilter);
      },
    graphsMap: (state) => new Map(state.graphs.map((graph) => [graph.properties.fid, graph]))
  }
};
